import { Button, Group, Modal, Stack, Table, Text, TextInput } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { type Schemas, api, call } from "../../api/client";
import { formatTs, isoToLocalInput, localToIso, notifyError, notifyOk } from "../../components/common";

/** Audited correction of a job's effective start/end; the recorded click times stay unchanged. */
export function JobTimeCorrection({ job, onClose }: { job: Schemas["JobOut"]; onClose: () => void }) {
  const qc = useQueryClient();
  const path = { job_id: job.id };
  const local = (ts: string | null) => (ts ? isoToLocalInput(new Date(ts)) : "");
  const [form, setForm] = useState({ start: local(job.effective_start), end: local(job.effective_end), reason: "" });
  const history = useQuery({
    queryKey: ["job-corrections", job.id],
    queryFn: () => call(api.GET("/api/v1/jobs/{job_id}/time-corrections", { params: { path } })),
  });
  const save = useMutation({
    mutationFn: () => {
      const body: Schemas["JobTimeCorrectionIn"] = { reason: form.reason };
      if (form.start && form.start !== local(job.effective_start)) body.start = localToIso(form.start);
      if (form.end && form.end !== local(job.effective_end)) body.end = localToIso(form.end);
      return call(api.POST("/api/v1/jobs/{job_id}/time-corrections", { params: { path }, body }));
    },
    onSuccess: (rows) => {
      notifyOk(`Corrected; ${rows[0]?.events_reattributed ?? 0} production events re-attributed`);
      void qc.invalidateQueries();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={`Correct times of job ${job.code}`} size="lg">
      <Stack>
        <Text size="sm" c="dimmed">
          Recorded by the system: start {formatTs(job.actual_start)}, end {formatTs(job.actual_end)}. These stay
          unchanged; production is attributed using the corrected times.
        </Text>
        <Group grow>
          <TextInput type="datetime-local" label="Effective start" value={form.start} disabled={!job.effective_start}
            onChange={(e) => setForm({ ...form, start: e.currentTarget.value })} />
          <TextInput type="datetime-local" label="Effective end" value={form.end} disabled={!job.effective_end}
            onChange={(e) => setForm({ ...form, end: e.currentTarget.value })} />
        </Group>
        <TextInput label="Reason (required)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.currentTarget.value })} />
        <Group justify="end">
          <Button disabled={form.reason.length < 5} loading={save.isPending} onClick={() => save.mutate()}>Save correction</Button>
        </Group>
        {(history.data?.length ?? 0) > 0 && (
          <Table striped>
            <Table.Thead><Table.Tr><Table.Th>When</Table.Th><Table.Th>Field</Table.Th><Table.Th>From</Table.Th>
              <Table.Th>To</Table.Th><Table.Th>Reason</Table.Th><Table.Th>Events</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>
              {history.data!.map((c) => (
                <Table.Tr key={c.id}>
                  <Table.Td>{formatTs(c.corrected_at)}</Table.Td><Table.Td>{c.field}</Table.Td>
                  <Table.Td>{formatTs(c.previous_value)}</Table.Td><Table.Td>{formatTs(c.new_value)}</Table.Td>
                  <Table.Td>{c.reason}</Table.Td><Table.Td>{c.events_reattributed}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Modal>
  );
}
